/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import type { Equal, ExpectTrue } from '../types/validation'

import type {
  BasePageContentAPI,
  MappedApiPageContent,
} from './base-page-content'
import { type ContentLink, ContentLinkSchema } from './content-link'
import { type Image, ImageSchema, type SimpleImage } from './image'

/* TypeScript */
export type ApiArticlePageContent = BasePageContentAPI & {
  summary: string
  categories: Category[]
  author?: Maybe<Author>
  updateDate: string
  wordCount?: Maybe<number>
  keywords?: Maybe<string[]>
  showTableOfContent?: Maybe<boolean>
}

export type ArticlePageContent = Omit<
  MappedApiPageContent<ApiArticlePageContent>,
  'updateDate'
> & {
  updateDate?: Maybe<Date>
}

export interface Category {
  title: string
  slug: string
  name?: Maybe<string>
}

export interface Author {
  id: string
  type: string
  hiddenDevices: string[]
  props: AuthorApiProps
}

type SocialIcon =
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'twitter'
  | 'medium'
  | 'link'
  | 'profile'
  | 'pinterest'
  | 'tiktok'
  | 'youtube'

export type SocialProfile = {
  id: string
  link: ContentLink
  icon: SocialIcon
}

export interface AuthorApiProps {
  technicalName?: Maybe<string>
  name: string
  role: string
  presentation: string
  image: Image
  profiles?: Maybe<SocialProfile[]>
}

export interface Article {
  id: number
  tagline: string
  title: string
  text: string
  link: ContentLink
  image: SimpleImage
  category: string
  translatedCategory: string
}

/* Schemas */
export const CategorySchema = z.object({
  title: z.string(),
  slug: z.string(),
  name: z.nullish(z.string()),
})

export const SocialIconSchema = z.enum([
  'facebook',
  'instagram',
  'linkedin',
  'twitter',
  'medium',
  'link',
  'profile',
  'pinterest',
  'tiktok',
  'youtube',
])

export const SocialProfileSchema = z.object({
  id: z.string(),
  link: ContentLinkSchema,
  icon: SocialIconSchema,
})

export const AuthorApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  name: z.string(),
  role: z.string(),
  presentation: z.string(),
  image: ImageSchema,
  profiles: z.nullish(z.array(SocialProfileSchema)),
})

/* Schemas validation */
type TestAuthorProps = ExpectTrue<
  Equal<AuthorApiProps, z.infer<typeof AuthorApiPropsSchema>>
>
